// pages/routing/RoutingClient.jsx — SOUND-826 Phase 1
//
// One client's CheckoutChamp routing, as of a stored snapshot. READ-ONLY: the
// only write on this page is Refresh, which asks sound-api to re-read CC (it
// stores a new snapshot only when something changed, otherwise it just stamps
// lastCheckedAt). Nothing here calls a CC create/update endpoint.
//
// URL state: ?tab=<objectType> (default profiles) and ?snapshot=<snapshotId>
// for an older snapshot (default latest), so a link reproduces the view.
//
// SOUND-827 Phase 2 adds PROPOSE MODE and the Changes tab. Propose mode only
// edits a client-side draft; "Submit proposal" posts a change set based on the
// snapshot being viewed (which must be the latest — the API 409s otherwise, and
// the draft is then re-based onto the newer snapshot). Nothing on this page
// writes to CheckoutChamp; GO lives on the review page (RoutingChangeSet.jsx).
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, AlertTriangle, Lock, Pencil, Route as RouteIcon } from "lucide-react";
import ConfirmDialog from "components/ConfirmDialog";
import PageHeader from "components/PageLayout";
import EmptyState from "components/EmptyState";
import Skeleton from "components/Skeleton";
import ListSearchBar from "components/Records/ListSearchBar";
import { useToast } from "components/Toast";
import {
  useRoutingClients,
  useRoutingSnapshot,
  useRoutingSnapshots,
  useRoutingRefresh,
  useRoutingChangeSetActions,
} from "hooks/useRouting";
import {
  ROUTING_OBJECT_TYPES,
  DEFAULT_ROUTING_TAB,
  isRoutingTab,
  objectTypeLabel,
  accessState,
  noAccessMessage,
  filterItems,
  changedBanner,
  formatTimestamp,
  refreshErrorMessage,
} from "utilities/routingView";
import {
  ProfilesTab,
  CascadesTab,
  BinMapsTab,
  BinProfilesTab,
  RecycleTab,
  GatewaysTab,
} from "./RoutingTabs";
import { EditableProfilesTab, GenericProposeTab, DraftTray } from "./RoutingPropose";
import RoutingChangeSets from "./RoutingChangeSets";
import {
  removeChange,
  buildChangesPayload,
  sumTo100Problems,
  rebaseDraft,
  submitError,
  warningText,
} from "utilities/routingChanges";

// Not an object type — the change sets list.
const CHANGES_TAB = "changes";

const SEARCH_PLACEHOLDER = {
  profiles: "Search profiles by name, gateway or MID",
  cascades: "Search cascades by name, gateway or MID",
  binMaps: "Search by name, BIN (or a card's first digits), bank, campaign or MID",
  binProfiles: "Search by BIN (or a card's first digits) or message",
  recycle: "Search by order type or MID",
  gateways: "Search by biller ID, title, descriptor, MID number or MID",
};

export default function RoutingClient() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();

  const tabParam = params.get("tab");
  const tab =
    tabParam === CHANGES_TAB ? CHANGES_TAB : isRoutingTab(tabParam) ? tabParam : DEFAULT_ROUTING_TAB;
  const snapshotId = params.get("snapshot") || null;

  // One search box per tab; switching tabs keeps each tab's query.
  const [queries, setQueries] = useState({});
  const query = queries[tab] || "";

  const { clients } = useRoutingClients();
  const client = clients.find((c) => c.companyId === companyId) || null;

  const { snapshot, loading, error, refetch } = useRoutingSnapshot(companyId, snapshotId);
  const { snapshots, refetch: refetchHistory } = useRoutingSnapshots(companyId);
  const { refresh, refreshing } = useRoutingRefresh();
  const { create, busy } = useRoutingChangeSetActions();

  // --- Propose mode (SOUND-827) -------------------------------------------
  const [proposing, setProposing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [draftBase, setDraftBase] = useState(null); // snapshotId the draft's `from`s came from
  const [confirmDiscard, setConfirmDiscard] = useState(null); // "discard" | "stop"
  const [submitOpen, setSubmitOpen] = useState(false);
  const [proposalTitle, setProposalTitle] = useState("");
  const [proposalNote, setProposalNote] = useState("");
  const [submitErr, setSubmitErr] = useState(null);

  const onDraft = (next) => {
    setDraft(next);
    if (snapshot?.snapshotId) setDraftBase(snapshot.snapshotId);
  };

  // A draft belongs to ONE client. React Router keeps this component mounted
  // across /routing/:companyId changes, so drop everything when it moves —
  // otherwise one client's draft could be submitted against another's.
  useEffect(() => {
    setProposing(false);
    setDraft([]);
    setDraftBase(null);
    setSubmitOpen(false);
    setSubmitErr(null);
  }, [companyId]);

  // A newer snapshot arrived (Refresh, or "load latest" after a 409) while a
  // draft exists: carry the draft onto it, dropping what moved underneath.
  useEffect(() => {
    // Never re-base onto an OLDER snapshot picked from history.
    if (snapshotId) return;
    if (!snapshot?.snapshotId || !draftBase || draftBase === snapshot.snapshotId) return;
    if (!draft.length) {
      setDraftBase(snapshot.snapshotId);
      return;
    }
    const { draft: kept, conflicts } = rebaseDraft(draft, snapshot);
    setDraft(kept);
    setDraftBase(snapshot.snapshotId);
    setSubmitErr(null);
    toast({
      status: conflicts.length ? "info" : "success",
      title: conflicts.length
        ? `Draft re-based — ${conflicts.length} change${conflicts.length === 1 ? "" : "s"} dropped`
        : "Draft re-based onto the latest snapshot",
      description: conflicts.length
        ? conflicts
            .slice(0, 3)
            .map((c) => `${c.change.field || c.change.op}: ${c.reason}`)
            .join("; ") + (conflicts.length > 3 ? ` (+${conflicts.length - 3} more)` : "")
        : "Every change still applies to what CheckoutChamp holds now.",
    });
    // Only a snapshot change triggers a re-base.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot?.snapshotId]);

  const sumProblems = useMemo(
    () => (proposing && snapshot ? sumTo100Problems(snapshot, draft) : []),
    [proposing, snapshot, draft]
  );

  const startProposing = () => {
    setProposing(true);
    if (tab === CHANGES_TAB) setParam("tab", null);
  };
  const stopProposing = () => {
    if (draft.length) setConfirmDiscard("stop");
    else setProposing(false);
  };
  const discard = (andStop) => {
    setDraft([]);
    setDraftBase(null);
    setConfirmDiscard(null);
    if (andStop) setProposing(false);
  };

  const handleSubmit = async () => {
    setSubmitErr(null);
    try {
      const body = await create(companyId, {
        title: proposalTitle.trim(),
        note: proposalNote.trim() || undefined,
        basedOnSnapshotId: snapshot.snapshotId,
        changes: buildChangesPayload(draft),
      });
      const cs = body.data;
      const warnings = (body.warnings || []).map(warningText).filter(Boolean);
      toast({
        status: warnings.length ? "info" : "success",
        title: "Proposal submitted",
        description: warnings.length
          ? `${warnings.length} warning${warnings.length === 1 ? "" : "s"}: ${warnings[0]}`
          : "An internal admin can now review and apply it.",
      });
      setSubmitOpen(false);
      setProposalTitle("");
      setProposalNote("");
      setDraft([]);
      setDraftBase(null);
      setProposing(false);
      if (cs?.changeSetId) navigate(`/routing/${companyId}/changes/${cs.changeSetId}`);
      else setParam("tab", CHANGES_TAB);
    } catch (err) {
      setSubmitErr(submitError(err));
    }
  };

  const loadLatestAndRebase = () => {
    setSubmitOpen(false);
    setSubmitErr(null);
    refetchHistory();
    refetch(); // the effect above re-bases when the snapshot id moves
  };

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: key === "tab" });
  };

  const handleRefresh = async () => {
    try {
      const { stored } = await refresh(companyId);
      toast({
        status: "success",
        title: stored ? "New snapshot stored" : "No changes",
        description: stored
          ? "CheckoutChamp routing differs from the previous snapshot."
          : "CheckoutChamp routing matches the latest snapshot.",
      });
      refetchHistory();
      // Back to the latest snapshot. Dropping ?snapshot= changes the URL and
      // refetches by itself; on latest already, refetch explicitly.
      if (snapshotId) setParam("snapshot", null);
      else refetch();
    } catch (err) {
      toast({
        status: err?.response?.status === 429 ? "info" : "destructive",
        title: err?.response?.status === 429 ? "Just refreshed" : "Refresh failed",
        description: refreshErrorMessage(err),
      });
    }
  };

  const tabs = [
    ...ROUTING_OBJECT_TYPES.map((t) => {
      const a = accessState(snapshot, t.key);
      const suffix = a.state === "ok" ? ` (${a.items.length})` : a.state === "denied" ? " —" : "";
      return { key: t.key, label: `${t.label}${snapshot ? suffix : ""}` };
    }),
    { key: CHANGES_TAB, label: "Changes" },
  ];

  const access = accessState(snapshot, tab);
  const visible = useMemo(
    () => filterItems(tab, access.items, query),
    [tab, access.items, query]
  );
  const profiles = accessState(snapshot, "profiles").items;
  const cascades = accessState(snapshot, "cascades").items;

  const banner = changedBanner(snapshot?.changedSincePrevious);
  const viewingOlder = !!snapshotId;

  const title = client?.name || client?.slug || (snapshot ? companyId : "Routing");
  const subtitle = snapshot
    ? `Snapshot taken ${formatTimestamp(snapshot.takenAt)} · checked ${formatTimestamp(
        snapshot.lastCheckedAt || snapshot.takenAt
      )}`
    : "CheckoutChamp gateway routing, mirrored read-only.";

  const gatewaysAccess = accessState(snapshot, "gateways");
  const gatewayList = gatewaysAccess.state === "ok" ? gatewaysAccess.items : null;

  const actions = (
    <>
      {snapshot && !viewingOlder && (
        <button
          type="button"
          className={proposing ? "btn btn-outline" : "btn btn-secondary"}
          onClick={proposing ? stopProposing : startProposing}
          title={proposing ? "Leave propose mode" : "Draft changes to this client's routing for an admin to review"}
        >
          <Pencil className="h-4 w-4" />
          {proposing ? "Stop proposing" : "Propose changes"}
        </button>
      )}
      {snapshots.length > 1 && !proposing && (
        <select
          className="select w-auto max-w-[260px] text-sm"
          aria-label="Snapshot history"
          value={snapshotId || ""}
          onChange={(e) => setParam("snapshot", e.target.value || null)}
        >
          <option value="">Latest snapshot</option>
          {snapshots.slice(1).map((s) => (
            <option key={s.snapshotId} value={s.snapshotId}>
              {formatTimestamp(s.takenAt)}
              {s.changedSincePrevious?.length
                ? ` · ${s.changedSincePrevious.map(objectTypeLabel).join(", ")}`
                : ""}
            </option>
          ))}
        </select>
      )}
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleRefresh}
        disabled={refreshing}
        title="Re-read this client's routing from CheckoutChamp now"
      >
        <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
        {refreshing ? "Refreshing…" : "Refresh"}
      </button>
      <button type="button" className="btn btn-ghost" onClick={() => navigate("/routing")}>
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
    </>
  );

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={actions}
        tabs={snapshot ? tabs : undefined}
        activeKey={tab}
        onTabChange={(key) => setParam("tab", key === DEFAULT_ROUTING_TAB ? null : key)}
      />

      <ConfirmDialog
        open={!!confirmDiscard}
        onCancel={() => setConfirmDiscard(null)}
        onConfirm={() => discard(confirmDiscard === "stop")}
        variant="destructive"
        title={`Discard ${draft.length} draft change${draft.length === 1 ? "" : "s"}?`}
        description="The draft lives only in this browser tab. Nothing has been sent to CheckoutChamp."
        confirmLabel="Discard"
      />

      <ConfirmDialog
        open={submitOpen}
        onCancel={() => {
          setSubmitOpen(false);
          setSubmitErr(null);
        }}
        onConfirm={handleSubmit}
        title={`Submit ${draft.length} change${draft.length === 1 ? "" : "s"} for review`}
        description="This creates a change set for an internal admin to tick and apply. Nothing is written to CheckoutChamp yet."
        confirmLabel={busy === "create" ? "Submitting…" : "Submit proposal"}
        confirmDisabled={!proposalTitle.trim() || !!busy || submitErr?.kind === "outdated"}
        size="md"
      >
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Title</span>
          <input
            className="input w-full"
            value={proposalTitle}
            maxLength={200}
            onChange={(e) => setProposalTitle(e.target.value)}
            placeholder="e.g. Shift 10% from Kurv to PayArc"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Note (optional)</span>
          <textarea
            className="textarea min-h-[72px] w-full"
            value={proposalNote}
            onChange={(e) => setProposalNote(e.target.value)}
            placeholder="Why — what you expect this to change"
          />
        </label>
        {sumProblems.length > 0 && (
          <p className="text-xs text-warning">
            {sumProblems.length} WEIGHTED profile{sumProblems.length === 1 ? "" : "s"} won't sum to 100% — the
            approver will have to untick changes before GO.
          </p>
        )}
        {submitErr && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm">
            <p className="font-medium text-destructive">{submitErr.message}</p>
            {submitErr.items.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-xs">
                {submitErr.items.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
            {submitErr.kind === "outdated" && (
              <button type="button" className="btn btn-outline mt-2" onClick={loadLatestAndRebase}>
                <RefreshCw className="h-4 w-4" />
                Load latest snapshot &amp; re-base the draft
              </button>
            )}
          </div>
        )}
      </ConfirmDialog>

      <div className="container space-y-4 py-6">
        {loading ? (
          <div className="space-y-3">
            <Skeleton.Rect height={40} width="100%" />
            <Skeleton.Rect height={180} width="100%" />
            <Skeleton.Rect height={180} width="100%" />
          </div>
        ) : error ? (
          <div className="surface p-8 text-center">
            <p className="text-destructive">
              {error?.response?.status === 404
                ? error?.response?.data?.error ||
                  "This company is not a CheckoutChamp routing client, or has no snapshot yet."
                : error?.response?.data?.error || error?.message || "Failed to load routing."}
            </p>
            <div className="mt-3 flex justify-center gap-2">
              <button className="btn btn-ghost" onClick={refetch}>
                Retry
              </button>
              {error?.response?.status === 404 && client && (
                <button className="btn btn-outline" onClick={handleRefresh} disabled={refreshing}>
                  Take first snapshot
                </button>
              )}
            </div>
          </div>
        ) : !snapshot ? (
          <EmptyState
            icon={RouteIcon}
            heading="No snapshot yet"
            description="Refresh to read this client's routing from CheckoutChamp for the first time."
            action={
              <button className="btn btn-primary" onClick={handleRefresh} disabled={refreshing}>
                <RefreshCw className="h-4 w-4" />
                Take first snapshot
              </button>
            }
          />
        ) : (
          <>
            {viewingOlder && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-info/30 bg-info/10 px-4 py-2 text-sm">
                <span>You are viewing an older snapshot ({formatTimestamp(snapshot.takenAt)}).</span>
                <button className="btn btn-ghost" onClick={() => setParam("snapshot", null)}>
                  View latest
                </button>
              </div>
            )}
            {banner && (
              <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-2 text-sm text-foreground">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                <span>{banner}</span>
              </div>
            )}

            {tab === CHANGES_TAB ? (
              <RoutingChangeSets companyId={companyId} />
            ) : access.state === "denied" ? (
              <div className="surface flex items-start gap-3 p-6">
                <Lock className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <div>
                  <p className="font-medium text-foreground">{noAccessMessage(tab)}</p>
                  {access.reason && (
                    <p className="mt-1 text-sm text-muted-foreground">CheckoutChamp said: {access.reason}</p>
                  )}
                </div>
              </div>
            ) : access.state === "missing" ? (
              <div className="surface p-6 text-sm text-muted-foreground">
                This snapshot holds no {objectTypeLabel(tab)} data.
              </div>
            ) : (
              <>
                <ListSearchBar
                  value={query}
                  onChange={(v) => setQueries((q) => ({ ...q, [tab]: v }))}
                  placeholder={SEARCH_PLACEHOLDER[tab]}
                >
                  <span className="text-sm text-muted-foreground">
                    {query ? `${visible.length} of ${access.items.length}` : `${access.items.length}`}{" "}
                    {objectTypeLabel(tab).toLowerCase()}
                  </span>
                </ListSearchBar>
                {proposing ? (
                  <ProposeBody
                    tab={tab}
                    items={visible}
                    filtered={!!query}
                    gateways={gatewayList}
                    draft={draft}
                    onDraft={onDraft}
                  />
                ) : (
                  <TabBody
                    tab={tab}
                    items={visible}
                    filtered={!!query}
                    profiles={profiles}
                    cascades={cascades}
                  />
                )}
              </>
            )}

            {proposing && (
              <DraftTray
                draft={draft}
                snapshot={snapshot}
                sumProblems={sumProblems}
                submitting={!!busy}
                onUndo={(key) => setDraft((d) => removeChange(d, key))}
                onDiscard={() => setConfirmDiscard("discard")}
                onSubmit={() => {
                  setSubmitErr(null);
                  setSubmitOpen(true);
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ProposeBody({ tab, items, filtered, gateways, draft, onDraft }) {
  if (tab === "profiles") {
    return items.length ? (
      <EditableProfilesTab items={items} gateways={gateways} draft={draft} onDraft={onDraft} />
    ) : (
      <div className="surface py-10 text-center text-sm text-muted-foreground">
        {filtered ? "No profiles match this search." : "No profiles configured."}
      </div>
    );
  }
  return <GenericProposeTab objectType={tab} items={items} filtered={filtered} draft={draft} onDraft={onDraft} />;
}

function TabBody({ tab, items, filtered, profiles, cascades }) {
  // The card tabs have no table empty state of their own.
  const cardEmpty = (label) => (
    <div className="surface py-10 text-center text-sm text-muted-foreground">
      {filtered ? `No ${label} match this search.` : `No ${label} configured.`}
    </div>
  );
  switch (tab) {
    case "profiles":
      return items.length ? <ProfilesTab items={items} cascades={cascades} /> : cardEmpty("profiles");
    case "cascades":
      return items.length ? <CascadesTab items={items} /> : cardEmpty("cascades");
    case "binMaps":
      return <BinMapsTab items={items} profiles={profiles} filtered={filtered} />;
    case "binProfiles":
      return <BinProfilesTab items={items} filtered={filtered} />;
    case "recycle":
      return items.length ? <RecycleTab items={items} /> : cardEmpty("recycle billing profiles");
    case "gateways":
      return <GatewaysTab items={items} filtered={filtered} />;
    default:
      return null;
  }
}
